'use client'

import type { CSSVariablesResolver, MantineTheme } from '@mantine/core'
import { rebrandCssResolver } from '@nile/mantine-theme/theme/rebrand'
import { mercyOpenSans } from '@/tenants/mercy/fonts/openSans'
import { MERCY_HEADING_SCALE } from './typography'
import { headingScaleVariables } from '@/config/tenants/tokens'
import { MERCY_CONTENT_PADDING } from './layout'

/** Mercy brand palette */
const MERCY_SURFACE = '#FFFFFF'
const MERCY_TEXT = '#000000'
const MERCY_TEXT_SECONDARY = '#4D4D4D'
export const MERCY_PRIMARY = '#0076A8'
const MERCY_PRIMARY_DARK = '#005a80'
const MERCY_ACCENT = '#00B5E2'

const MERCY_FONT_FAMILY = `${mercyOpenSans.style.fontFamily}, var(--font-plus-jakarta-sans), sans-serif`
const MERCY_WEIGHT_TEXT_BOLD = '600'

/**
 * Mantine CSS variable overrides for Mercy. Layers on rebrandCssResolver with
 * tenant color, radius, and surface tokens.
 *
 * The CSS resolver outputs declarations at the :root level. For any Mantine tokens that have already been declared in
 * customer-frontend/globals.css at the body level, use mercyGlobal.css instead.
 */
export const mercyCssResolver: CSSVariablesResolver = (theme: MantineTheme) => {
    const base = rebrandCssResolver(theme)

    return {
        variables: {
            ...base.variables,

            '--mercy-primary': MERCY_PRIMARY,
            '--mercy-primary-tint': 'color-mix(in srgb, var(--mercy-primary) 10%, transparent)',

            // GM surfaces and palette tokens
            '--gm-bg-solid': MERCY_SURFACE,
            '--gm-bg-primary': MERCY_SURFACE,
            '--gm-background-bg-surface': MERCY_SURFACE,
            '--gm-background-bg-tonal': 'var(--mercy-primary-tint)',
            '--gm-gray-lighter': MERCY_SURFACE,
            '--gm-gray-0': MERCY_SURFACE,

            // TODO: We should phase out overriding color tokens like --gm-blue-6 directly, and instead override more specific tokens like --gm-foreground-fg-highlight
            '--gm-blue-6': MERCY_PRIMARY,
            '--gm-foreground-fg-highlight': MERCY_PRIMARY,
            '--gm-foreground-fg-primary': MERCY_TEXT,
            '--gm-foreground-fg-outline-primary': 'var(--mercy-neutral-gray)',
            '--gm-bg-highlight-cont': 'var(--mercy-primary-tint)',
            '--gm-search-result-card-skeleton-bg': '#D9D9D9',
            '--gm-foreground-fg-accent': MERCY_PRIMARY,
            '--gm-choice-card-border': MERCY_PRIMARY,

            '--gm-weight-text-bold': MERCY_WEIGHT_TEXT_BOLD,
            ...headingScaleVariables(MERCY_HEADING_SCALE, (level, step, out) => {
                out[`--gm-heading-${level}-font-weight`] = 'var(--gm-weight-text-bold)'

                if (step.letterSpacing) {
                    out[`--gm-heading-${level}-letter-spacing`] = step.letterSpacing
                }

                return out
            }),
            '--gm-font-family': MERCY_FONT_FAMILY,
            '--path-blue': MERCY_PRIMARY,
            '--path-text-gray': MERCY_TEXT,

            // Mercy brand tokens (used by storefront and login components)
            '--mercy-primary-dark': MERCY_PRIMARY_DARK,
            '--mercy-accent': MERCY_ACCENT,
            '--mercy-bg-blue': MERCY_PRIMARY,
            '--mercy-bg-blue-hover': '#006894',
            '--mercy-bg-orange': '#BF5412',
            '--mercy-gray': '#747474',
            '--mercy-gray-light': '#f3f4f4',
            '--mercy-teal': '#155a6f',
            '--mercy-blue-light': '#d1e3e9',
            '--mercy-image-fallback-bg': '#d3d3d3',
            '--mercy-text-secondary': MERCY_TEXT_SECONDARY,
            '--mercy-neutral-gray': '#CCCCCC',
            '--mercy-light-orange': '#FDE1B9',
            '--mercy-dark-orange': '#7A4800',
            '--mercy-dark-green': '#087442',
            '--mercy-light-green': '#D3EBDF',
            '--mercy-dark-red': '#7A1A1A',
            '--mercy-light-red': '#F1BCBF',

            '--mercy-bg-neutral-container': 'color-mix(in srgb, #FDA712 15%, transparent)',
            '--mercy-storefront-content-padding': `${MERCY_CONTENT_PADDING}px`,
            '--mercy-footer-bg': MERCY_PRIMARY_DARK,

            // Availability slot preview tiles (IPV providers scroll row)
            '--availability-slot-tile-width': 'calc(var(--gm-spacing-48) * 2)',

            // Radius
            '--mantine-radius-lg': '16px',
            '--mantine-radius-xl': 'var(--gm-radius-4)',
            '--paper-radius': 'var(--gm-radius-4)',
        },
        light: {
            ...base.light,

            // Page chrome
            '--mantine-color-body': 'var(--gm-bg-solid)',
            '--mantine-color-text': MERCY_TEXT,

            // Primary (rebrand `primaryColor: 'blue'`)
            '--mantine-color-blue-filled': MERCY_PRIMARY,
            '--mantine-color-blue-5': MERCY_PRIMARY,
            '--mantine-color-blue-6': MERCY_PRIMARY,
            '--mantine-color-blue-7': MERCY_PRIMARY_DARK,

            // Accent
            '--mantine-color-cyan-filled': MERCY_ACCENT,
            '--mantine-color-cyan-6': MERCY_ACCENT,
        },
        dark: {
            ...base.dark,
            '--mantine-color-body': 'var(--gm-bg-solid)',
        },
    }
}
